import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { ContentSection } from '../../services/trainingService';

interface LessonRendererProps {
  contentSections: ContentSection[];
  keyTakeaways?: string[];
}

const LessonRenderer: React.FC<LessonRendererProps> = ({ contentSections, keyTakeaways }) => {
  const renderIcon = (iconName?: string) => {
    switch (iconName) {
      case 'check':
        return <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
      case 'verified':
        return <VerifiedIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
      case 'info':
        return <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
      default:
        return <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
    }
  };

  const renderSection = (section: ContentSection, index: number) => {
    const { type, title, content, style } = section;

    switch (type) {
      case 'text':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            {title && (
              <Typography variant="h6" gutterBottom sx={{ color: style?.color || '#003366' }}>
                {title}
              </Typography>
            )}
            {content.map((text, i) => (
              <Typography key={i} variant="body1" paragraph>
                {text}
              </Typography>
            ))}
          </Box>
        );

      case 'list':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            {title && (
              <Typography variant="h6" gutterBottom sx={{ color: style?.color || '#003366' }}>
                {title}
              </Typography>
            )}
            <List dense>
              {content.map((item, i) => (
                <ListItem key={i}>
                  <ListItemIcon>{renderIcon(style?.icon)}</ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 'card':
        return (
          <Card key={index} sx={{ mb: 3, border: `2px solid ${style?.color || '#003366'}` }}>
            <CardContent>
              {title && (
                <Typography variant="h6" gutterBottom sx={{ color: style?.color || '#003366' }}>
                  {title}
                </Typography>
              )}
              {content.map((text, i) => (
                <Typography key={i} variant="body2" paragraph={i < content.length - 1}>
                  {text}
                </Typography>
              ))}
            </CardContent>
          </Card>
        );

      case 'alert':
        return (
          <Alert
            key={index}
            severity={style?.severity || 'info'}
            sx={{ mb: 3 }}
            icon={style?.icon ? renderIcon(style.icon) : undefined}
          >
            {title && (
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {title}
              </Typography>
            )}
            {content.map((text, i) => (
              <Typography key={i} variant="body2" paragraph={i < content.length - 1}>
                {text}
              </Typography>
            ))}
          </Alert>
        );

      case 'code':
        return (
          <Paper
            key={index}
            sx={{
              mb: 3,
              p: 2,
              bgcolor: '#f5f5f5',
              fontFamily: 'monospace',
              overflow: 'auto'
            }}
          >
            {title && (
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {title}
              </Typography>
            )}
            {content.map((code, i) => (
              <Typography key={i} variant="body2" component="pre" sx={{ m: 0 }}>
                {code}
              </Typography>
            ))}
          </Paper>
        );

      case 'image':
        return (
          <Box key={index} sx={{ mb: 3, textAlign: 'center' }}>
            {title && (
              <Typography variant="h6" gutterBottom sx={{ color: style?.color || '#003366' }}>
                {title}
              </Typography>
            )}
            <img
              src={content[0]}
              alt={title || 'Lesson image'}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
            {content[1] && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: '#71767A' }}>
                {content[1]}
              </Typography>
            )}
          </Box>
        );

      case 'video':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            {title && (
              <Typography variant="h6" gutterBottom sx={{ color: style?.color || '#003366' }}>
                {title}
              </Typography>
            )}
            <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={content[0]}
                title={title || 'Lesson video'}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allowFullScreen
              />
            </Box>
            {content[1] && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: '#71767A' }}>
                {content[1]}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {contentSections.map((section, index) => renderSection(section, index))}

      {keyTakeaways && keyTakeaways.length > 0 && (
        <Paper sx={{ p: 3, bgcolor: '#f0f7ff', border: '2px solid #003366', mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            Key Takeaways
          </Typography>
          <List dense>
            {keyTakeaways.map((takeaway, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText primary={takeaway} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default LessonRenderer;
